import api from '@/soluqr/api/client';
import { logStructured } from '@/lib/networkLog';
import { normalizeCustomerPhone } from '@/lib/phone';
import { saveLocalCustomer, saveCustomerSessionFromApi } from '@/lib/localCustomer';
import { withCustomerSession } from '@/lib/customerSession';
import { saveLastOrder } from '@/lib/localLastOrder';
import { toast } from 'sonner';
import {
  parseCashAmountInput,
  buildEncodedWhatsAppOrderText,
  buildWhatsAppOrderUrl,
  decodeWhatsAppEncodedText,
} from './whatsappOrder';
import { absoluteCheckoutTrackingUrl } from './trackingUrl';
import { computeCheckoutTotals } from './totals';
import { shouldRedirectToMercadoPagoOnline } from './paymentMethods';
import { saveMpOrderReturnSession } from './mpReturnSession';
import { getPublicMenuPath } from '@/lib/tenant/getPublicMenuPath';
import { productIncludedFieldsForOrderLine } from '@/lib/productComposition';

const REGISTER_ORDER_FAILED_MSG = 'No se pudo registrar el pedido. Intenta nuevamente.';
const CHECKOUT_ALREADY_SENDING_MSG =
  'Tu pedido ya se está enviando. No pulses otra vez.';

/** Evita dos POST register-order si el cliente hace doble clic antes de que el UI se bloquee. */
let checkoutSubmitInFlight = false;

function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function axiosErrorDetail(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  if (d && typeof d === 'object' && d.msg) return d.msg;
  return null;
}

const INVENTORY_INSUFFICIENT_MSG =
  'No hay stock suficiente para uno o más productos.';

/**
 * Mensaje amigable para 409 inventory_insufficient_stock (sin PII extra).
 */
export function formatInventoryInsufficientStockError(err) {
  const d = err?.response?.data?.detail;
  if (!d || typeof d !== 'object' || d.code !== 'inventory_insufficient_stock') {
    return null;
  }
  const base =
    typeof d.message === 'string' && d.message.trim()
      ? d.message.trim()
      : INVENTORY_INSUFFICIENT_MSG;
  const items = Array.isArray(d.items) ? d.items : [];
  const names = items
    .map((it) => String(it?.product_name_snapshot || '').trim())
    .filter(Boolean);
  if (!names.length) return base;
  const shown = names.slice(0, 5);
  const extra = names.length > 5 ? ` (+${names.length - 5} más)` : '';
  return `${base} ${shown.join(', ')}${extra}`;
}

/**
 * Validaciones equivalentes a CheckoutPage.handleSendOrder (previo a APIs).
 */
export function validateCheckoutPayload(p) {
  const {
    customerPhone,
    serviceType,
    address,
    markerPosition,
    deliveryLoading,
    deliveryQuote,
    tableNumber,
    paymentMethod,
    onlineManualKind,
    cashAmount,
    proofFile,
    proofUrl,
    getCartTotal,
    tip,
    /** true si el negocio usa zonas por dirección (no mapa + API de distancia). */
    useAddressZonesForDelivery,
  } = p;

  if (!customerPhone) {
    return { ok: false, error: 'Ingresa tu número de teléfono' };
  }
  const phoneNorm = normalizeCustomerPhone(customerPhone);
  if (!phoneNorm || phoneNorm.length < 8) {
    return { ok: false, error: 'Ingresa un número de teléfono válido' };
  }

  if (serviceType === 'delivery' && !address) {
    return { ok: false, error: 'Ingresa tu dirección' };
  }

  if (serviceType === 'delivery' && useAddressZonesForDelivery) {
    if (deliveryLoading) {
      return { ok: false, error: 'Espera el cálculo del costo de envío' };
    }
    if (deliveryQuote?.no_zone_match) {
      return {
        ok: false,
        error: 'No tenemos cobertura para esta zona. Probá otra dirección o consultá al local.',
      };
    }
    if (typeof deliveryQuote?.delivery_fee !== 'number') {
      return {
        ok: false,
        error: 'Escribí calle, número o comuna para confirmar si hay cobertura y el costo de envío.',
      };
    }
  } else if (serviceType === 'delivery' && !useAddressZonesForDelivery) {
    const addr = String(address || '').trim();
    if (!markerPosition) {
      if (addr.length >= 8) {
        return {
          ok: false,
          error: 'Espera a que ubiquemos tu dirección o revisá el texto ingresado',
        };
      }
      return { ok: false, error: 'Escribí tu dirección completa para calcular el envío' };
    }
    if (deliveryLoading) {
      return { ok: false, error: 'Espera el cálculo del costo de envío' };
    }
    if (typeof deliveryQuote?.delivery_fee !== 'number') {
      return { ok: false, error: 'No se pudo calcular el envío. Revisa la dirección o intenta de nuevo.' };
    }
  }

  if (serviceType === 'mesa' && !tableNumber) {
    return { ok: false, error: 'Ingresa el número de mesa' };
  }

  if (paymentMethod === 'transferencia' && proofFile && !proofUrl) {
    return { ok: false, error: 'Espera a que se suba el comprobante' };
  }

  const { subtotal: subtotalSnap, delivery: deliverySnap, total: totalSnap } = computeCheckoutTotals({
    serviceType,
    deliveryQuote,
    getCartTotal,
    tip,
  });
  const cashAmountParsed = paymentMethod === 'efectivo' ? parseCashAmountInput(cashAmount) : null;

  if (paymentMethod === 'efectivo') {
    const rawCash = String(cashAmount || '').trim();
    if (rawCash) {
      if (cashAmountParsed == null) {
        return {
          ok: false,
          error: 'Indica un monto válido en “Con cuánto pagas”, o déjalo vacío',
        };
      }
      if (cashAmountParsed < totalSnap) {
        return { ok: false, error: 'El monto debe ser igual o mayor al total del pedido' };
      }
    }
  }

  return {
    ok: true,
    phoneNorm,
    totalSnap,
    cashAmountParsed,
    subtotalSnap,
    deliverySnap,
  };
}

/**
 * Upsert cliente, referidos, canjes, register-order, snapshot beneficios, guarda local y arma URL WhatsApp.
 * No limpia carrito ni notas (lo hace el caller).
 *
 * @param {object} p — mismos campos que validateCheckoutPayload + businessId, business, cart, customerName, customerEmail, orderNotes, checkoutReferralCode, checkoutRewardChoice, referralInfo, benefitsProgress, notifyError?, notifySuccess?
 *
 * Si POST /customers/register-order falla o no devuelve order_id, devuelve ok:false y no arma WhatsApp ni guarda último pedido.
 */
export async function submitCheckoutToWhatsApp(p) {
  const v = validateCheckoutPayload(p);
  if (!v.ok) return v;

  if (checkoutSubmitInFlight) {
    return { ok: false, error: CHECKOUT_ALREADY_SENDING_MSG, duplicate: true };
  }
  checkoutSubmitInFlight = true;
  try {
    return await submitCheckoutToWhatsAppUnlocked(p, v);
  } finally {
    checkoutSubmitInFlight = false;
  }
}

/**
 * @param {object} p
 * @param {{ ok: true, phoneNorm: string, totalSnap: number, cashAmountParsed: number|null, subtotalSnap: number, deliverySnap: number }} v
 */
async function submitCheckoutToWhatsAppUnlocked(p, v) {
  const {
    businessId,
    business,
    cart,
    getCartTotal,
    customerName,
    customerEmail,
    serviceType,
    tableNumber,
    address,
    markerPosition,
    orderNotes,
    tip,
    paymentMethod,
    onlineManualKind = null,
    cashAmount,
    proofUrl,
    deliveryQuote,
    checkoutReferralCode,
    checkoutRewardChoice,
    referralInfo,
    benefitsProgress,
    notifyError,
    notifySuccess,
  } = p;

  const phoneNorm = v.phoneNorm;
  const totalSnap = v.totalSnap;
  const cashAmountParsed = v.cashAmountParsed;

  const toastErr = typeof notifyError === 'function' ? notifyError : () => {};
  const toastSuccess = typeof notifySuccess === 'function' ? notifySuccess : () => {};

  const clubAvailable =
    benefitsProgress?.enabled && Number(benefitsProgress?.available_rewards) > 0;
  const clubLabelForRedeem =
    (benefitsProgress?.reward_text && String(benefitsProgress.reward_text).trim()) ||
    (benefitsProgress?.benefit_name && String(benefitsProgress.benefit_name).trim()) ||
    'Premio del club';
  const referralAvailable =
    referralInfo?.customer_found && Number(referralInfo?.available_referral_rewards) > 0;
  const referralLabelForRedeem =
    (referralInfo?.reward_text && String(referralInfo.reward_text).trim()) || 'Premio por referido';

  let appliedPremioLabel = null;

  try {
    const { data: upsertData } = await api.post('/customers/upsert', {
      business_id: businessId,
      phone: phoneNorm,
      name: (customerName || '').trim() || 'Cliente',
      email: (customerEmail || '').trim() || undefined,
      address:
        serviceType === 'delivery' && address ? address.trim() : undefined,
    });
    saveCustomerSessionFromApi(businessId, upsertData);
  } catch (upErr) {
    console.error('checkout upsert (no bloquea registro de pedido)', upErr);
    toastErr(axiosErrorDetail(upErr) || 'No se pudo guardar tu perfil; seguimos con el pedido.');
  }

  const refCode = String(checkoutReferralCode || '').trim().toUpperCase();
  const alreadyReferred = Boolean(referralInfo?.referred_by);
  if (refCode.length >= 4 && !alreadyReferred) {
    try {
      await api.post(
        '/customers/apply-referral',
        {
          business_id: businessId,
          phone: phoneNorm,
          referral_code: refCode,
        },
        withCustomerSession({}, businessId),
      );
      toastSuccess('¡Código de referido aplicado!');
    } catch (refErr) {
      const d = refErr.response?.data?.detail;
      const msg =
        typeof d === 'string'
          ? d
          : Array.isArray(d)
            ? d[0]?.msg
            : 'No se pudo aplicar el código de referido';
      toastErr(msg);
    }
  }

  const clubRewardUnitsInCart = (cart || []).reduce(
    (s, line) => s + (line.isClubReward ? Number(line.quantity) || 1 : 0),
    0
  );

  if (checkoutRewardChoice === 'club' && clubAvailable && clubRewardUnitsInCart === 0) {
    try {
      await api.post(
        '/customers/redeem-benefit',
        {
          business_id: businessId,
          phone: phoneNorm,
          count: 1,
        },
        withCustomerSession({}, businessId),
      );
      appliedPremioLabel = clubLabelForRedeem;
    } catch (redeemErr) {
      const d = redeemErr.response?.data?.detail;
      const msg =
        typeof d === 'string'
          ? d
          : Array.isArray(d)
            ? d[0]?.msg
            : 'No se pudo canjear el premio del club';
      toastErr(msg);
    }
  } else if (checkoutRewardChoice === 'referral' && referralAvailable) {
    try {
      await api.post(
        '/customers/redeem-referral-reward',
        {
          business_id: businessId,
          phone: phoneNorm,
        },
        withCustomerSession({}, businessId),
      );
      appliedPremioLabel = referralLabelForRedeem;
    } catch (redeemErr) {
      const d = redeemErr.response?.data?.detail;
      const msg =
        typeof d === 'string'
          ? d
          : Array.isArray(d)
            ? d[0]?.msg
            : 'No se pudo canjear el premio por referido';
      toastErr(msg);
    }
  }

  const providedKey =
    typeof p.idempotencyKey === 'string' ? p.idempotencyKey.trim() : '';
  const idempotencyKey = providedKey || newIdempotencyKey();

  const orderSnapshot = {
    idempotency_key: idempotencyKey,
    customer_name: (customerName || '').trim() || 'Cliente',
    customer_email: (customerEmail || '').trim() || undefined,
    customer_address:
      serviceType === 'delivery' && address ? String(address).trim() : undefined,
    service_type: serviceType,
    table_number:
      tableNumber != null && String(tableNumber).trim() !== ''
        ? Number(tableNumber)
        : undefined,
    payment_method: paymentMethod,
    online_manual_channel:
      paymentMethod === 'online_manual' && (onlineManualKind === 'mercadopago' || onlineManualKind === 'card')
        ? onlineManualKind === 'mercadopago'
          ? 'mercadopago'
          : 'card'
        : undefined,
    cash_amount: cashAmountParsed != null ? cashAmountParsed : undefined,
    proof_url: proofUrl || undefined,
    address: serviceType === 'delivery' && address ? String(address).trim() : undefined,
    marker_position:
      markerPosition != null &&
      markerPosition.lat != null &&
      markerPosition.lng != null
        ? { lat: Number(markerPosition.lat), lng: Number(markerPosition.lng) }
        : undefined,
    delivery_fee: v.deliverySnap,
    delivery_quote_snapshot: deliveryQuote || undefined,
    // Inventario 21.x: product_id + quantity serán base del descuento futuro; hoy no descuenta stock.
    items: (cart || []).map((line) => {
      const qty = Number(line.quantity) || 1;
      const unit = Number(line.price) || 0;
      return {
        product_id: String(line.product?.id ?? ''),
        name: String(line.product?.name ?? 'Producto'),
        quantity: qty,
        unit_price: unit,
        line_subtotal: unit * qty,
        add_ons: Array.isArray(line.addOns) ? line.addOns : [],
        removed_ingredients: Array.isArray(line.removedIngredients)
          ? line.removedIngredients
          : [],
        ...productIncludedFieldsForOrderLine(line.product),
        is_club_reward: Boolean(line.isClubReward),
        club_reward_label: line.clubRewardLabel || undefined,
      };
    }),
    subtotal: v.subtotalSnap,
    tip: Number(tip) || 0,
    total: totalSnap,
    source: p.checkoutSource || 'menu_checkout',
    order_notes: (orderNotes || '').trim() || undefined,
  };

  let registerOrderRes;
  try {
    const res = await api.post('/customers/register-order', {
      business_id: businessId,
      phone: phoneNorm,
      order_summary: {
        customer_name: (customerName || '').trim() || 'Cliente',
        total: totalSnap,
        service_type: serviceType,
        payment_method: paymentMethod,
        ...(paymentMethod === 'online_manual' && (onlineManualKind === 'mercadopago' || onlineManualKind === 'card')
          ? {
              online_manual_channel: onlineManualKind === 'mercadopago' ? 'mercadopago' : 'card',
            }
          : {}),
        ...(cashAmountParsed != null ? { cash_amount: cashAmountParsed } : {}),
      },
      order_snapshot: orderSnapshot,
    });
    registerOrderRes = res.data;
  } catch (regErr) {
    console.error('register-order (crítico)', regErr);
    const st = regErr?.response?.status;
    const inventoryErr = formatInventoryInsufficientStockError(regErr);
    logStructured('register_order_failed', {
      businessId,
      status: st,
      code: regErr?.code,
      detail: inventoryErr || axiosErrorDetail(regErr),
    });
    if (inventoryErr) {
      return { ok: false, error: inventoryErr };
    }
    const detail = axiosErrorDetail(regErr);
    if (st >= 400 && st < 500) {
      return { ok: false, error: detail || REGISTER_ORDER_FAILED_MSG };
    }
    return { ok: false, error: REGISTER_ORDER_FAILED_MSG };
  }

  const orderIdFromRegister = registerOrderRes?.order_id;
  if (orderIdFromRegister == null || orderIdFromRegister === '') {
    console.error('register-order sin order_id', registerOrderRes);
    logStructured('checkout', {
      event: 'register_order_missing_id',
      businessId,
    });
    return { ok: false, error: REGISTER_ORDER_FAILED_MSG };
  }

  const trackingUrl =
    typeof registerOrderRes?.tracking_url === 'string' && registerOrderRes.tracking_url.trim() !== ''
      ? registerOrderRes.tracking_url.trim()
      : null;
  const customerTrackingAbsoluteUrl = trackingUrl ? absoluteCheckoutTrackingUrl(trackingUrl) : '';

  logStructured('analytics', {
    event: 'order_registered',
    businessId,
    order_id: String(orderIdFromRegister),
    total: totalSnap,
  });

  // Cobro online OAuth: crear preferencia con token del local y redirigir a Checkout Pro
  if (shouldRedirectToMercadoPagoOnline(business, onlineManualKind)) {
    try {
      // Armar WhatsApp/copiar ANTES del redirect para restaurar al volver de MP
      const encodedForMp = buildEncodedWhatsAppOrderText({
        // Forzar copy de pago online en el mensaje post-MP
        business: { ...business, mercadopago_online: true },
        cart,
        getCartTotal,
        customerName,
        customerPhone: phoneNorm,
        serviceType,
        tableNumber,
        address,
        markerPosition,
        orderNotes,
        tip,
        paymentMethod,
        onlineManualKind: 'mercadopago',
        cashAmount,
        proofUrl,
        deliveryQuote,
        referralInfo,
        benefitsSnapshot: null,
        appliedPremioLabel,
        clubSummaryLine: '',
        customerTrackingAbsoluteUrl: customerTrackingAbsoluteUrl || null,
      });
      const orderTextPlainMp = decodeWhatsAppEncodedText(encodedForMp);
      const whatsappUrlMp = buildWhatsAppOrderUrl(business, encodedForMp);

      const returnBase =
        typeof window !== 'undefined' && window.location?.origin
          ? `${window.location.origin}${getPublicMenuPath(
              business?.slug || businessId,
              'checkout',
            )}`
          : undefined;

      const { data: pref } = await api.post(
        `/payments/mercadopago/orders/${encodeURIComponent(String(orderIdFromRegister))}/create-preference`,
        returnBase ? { return_base_url: returnBase } : {},
      );
      const checkoutUrl =
        (typeof pref?.checkout_url === 'string' && pref.checkout_url.trim()) ||
        (typeof pref?.init_point === 'string' && pref.init_point.trim()) ||
        (typeof pref?.sandbox_init_point === 'string' && pref.sandbox_init_point.trim()) ||
        '';
      if (!checkoutUrl) {
        return {
          ok: false,
          error: 'No se pudo iniciar el pago con Mercado Pago. Intentá de nuevo o elegí otro método.',
        };
      }
      logStructured('analytics', {
        event: 'mp_order_checkout_redirect',
        businessId,
        order_id: String(orderIdFromRegister),
      });
      saveLocalCustomer(
        {
          name: (customerName || '').trim(),
          phone: phoneNorm,
          email: (customerEmail || '').trim(),
          ...(serviceType === 'delivery' && String(address || '').trim()
            ? { address: String(address).trim() }
            : {}),
        },
        businessId,
      );
      saveMpOrderReturnSession(businessId, {
        orderId: String(orderIdFromRegister),
        trackingUrl,
        totalSnap,
        orderTextPlain: orderTextPlainMp,
        whatsappUrl: whatsappUrlMp || null,
        whatsappUnavailable: !whatsappUrlMp,
      });
      return {
        ok: true,
        mercadopagoCheckoutUrl: checkoutUrl,
        orderId: orderIdFromRegister,
        phoneNorm,
        totalSnap,
        trackingUrl,
        orderTextPlain: orderTextPlainMp,
        whatsappUrl: whatsappUrlMp || null,
        whatsappUnavailable: !whatsappUrlMp,
        skipWhatsApp: true,
      };
    } catch (mpErr) {
      console.error('mp create-preference', mpErr);
      const detail = axiosErrorDetail(mpErr);
      return {
        ok: false,
        error: detail || 'No se pudo iniciar el pago con Mercado Pago. Intentá de nuevo.',
      };
    }
  }

  let whatsappClubLine = '';
  let benefitsSnapshotForMessage = null;
  try {
    const { data: bp } = await api.get(
      '/customers/benefits-progress',
      withCustomerSession({ params: { business_id: businessId, phone: phoneNorm } }, businessId),
    );
    benefitsSnapshotForMessage = bp;
    if (bp?.enabled) {
      const ar = Math.max(0, Number(bp.available_rewards) || 0);
      whatsappClubLine = `*Club de beneficios:* ${bp.total_orders} compras`;
      if (ar > 0) {
        whatsappClubLine += ar === 1 ? ' — 1 premio disponible' : ` — ${ar} premios disponibles`;
      } else {
        whatsappClubLine += ` — próximo premio en ${bp.remaining_orders} compra${bp.remaining_orders === 1 ? '' : 's'}`;
      }
    }
  } catch (e) {
    console.error('benefits-progress whatsapp', e);
  }

  saveLocalCustomer(
    {
      name: (customerName || '').trim(),
      phone: phoneNorm,
      email: (customerEmail || '').trim(),
      ...(serviceType === 'delivery' && String(address || '').trim()
        ? { address: String(address).trim() }
        : {}),
    },
    businessId
  );

  saveLastOrder(
    {
      businessId,
      businessName: business?.name || '',
      items: (cart || []).map(({ product, addOns, removedIngredients, quantity, price }) => ({
        product,
        addOns: addOns || [],
        removedIngredients: removedIngredients || [],
        quantity: Number(quantity) || 1,
        price: Number(price) || 0,
      })),
      serviceType,
      total: totalSnap,
      createdAt: new Date().toISOString(),
      paymentMethod,
      tableNumber: serviceType === 'mesa' ? tableNumber : '',
      address: serviceType === 'delivery' ? String(address || '').trim() : '',
      customerName: (customerName || '').trim(),
      customerPhone: phoneNorm,
      ...(markerPosition && typeof markerPosition.lat === 'number'
        ? { markerPosition: { lat: markerPosition.lat, lng: markerPosition.lng } }
        : {}),
      ...(cashAmountParsed != null ? { cash_amount: cashAmountParsed } : {}),
    },
    businessId,
    business
  );

  const encoded = buildEncodedWhatsAppOrderText({
    business,
    cart,
    getCartTotal,
    customerName,
    customerPhone: phoneNorm,
    serviceType,
    tableNumber,
    address,
    markerPosition,
    orderNotes,
    tip,
    paymentMethod,
    onlineManualKind: paymentMethod === 'online_manual' ? onlineManualKind : null,
    cashAmount,
    proofUrl,
    deliveryQuote,
    referralInfo,
    benefitsSnapshot: benefitsSnapshotForMessage,
    appliedPremioLabel,
    clubSummaryLine: whatsappClubLine,
    customerTrackingAbsoluteUrl: customerTrackingAbsoluteUrl || null,
  });

  const orderTextPlain = decodeWhatsAppEncodedText(encoded);
  const whatsappUrl = buildWhatsAppOrderUrl(business, encoded);

  if (!whatsappUrl) {
    console.warn('[checkout] Pedido registrado pero sin WhatsApp', {
      businessId,
      orderId: orderIdFromRegister,
      reason: 'missing_or_invalid_whatsapp',
    });
    logStructured('analytics', {
      event: 'whatsapp_failed',
      businessId,
      order_id: String(orderIdFromRegister),
      reason: 'missing_business_whatsapp',
    });
    toast.warning('Este local no tiene WhatsApp configurado');
    return {
      ok: true,
      whatsappUrl: null,
      whatsappUnavailable: true,
      orderTextPlain,
      orderId: orderIdFromRegister,
      phoneNorm,
      totalSnap,
      cashAmountParsed,
      appliedPremioLabel,
      trackingUrl,
    };
  }

  return {
    ok: true,
    whatsappUrl,
    whatsappUnavailable: false,
    orderTextPlain,
    orderId: orderIdFromRegister,
    phoneNorm,
    totalSnap,
    cashAmountParsed,
    appliedPremioLabel,
    trackingUrl,
  };
}
